import { handleActions } from 'redux-actions';
import { SubmissionError } from 'redux-form';

import find from 'lodash/find';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';

import http from '../../../shared/http';
import endpoints from '../../../../config/endpoints';

import { enum as bannerTypes } from '../../../../../../../enums/banner-types';
import { enum as languages } from '../../../../../../../enums/languages';

import { patterns } from '../../../shared/form/validation';

export const namespace = 'bannersContentEditor';

// action types
const FETCH_DATA_SUCCESS = `${namespace}/fetch-data-success`;
const SET_INITIAL_VALUES = `${namespace}/set-initial-values`;
const FETCH_DATA_ERROR = `${namespace}/fetch-data-error`;
const CHANGE_LANGUAGE = `${namespace}/change-language`;
const TOGGLE_PROGRESS_BAR = `${namespace}/toggle-progress-bar`;

export function isRequiredBannerLanguage(language) {
	return includes([languages.en, languages.ru], language);
}

// initial state
const initialState = {
	data: null,
	isLoading: true,
	isError: false,
	language: languages.en
};

const initialFormValues = {
	title: '',
	content: reduce(languages, (acc, lang) => ({
		...acc,
		[lang]: {
			list: isRequiredBannerLanguage(lang) ? [{
				type: [{ value: bannerTypes.simple, label: bannerTypes.simple }],
				link: null,
				image: '',
				blank: false
			}] : [],
			interval: null
		}
	}), {})
};

function mapResponseDataToStateData(data, { bannerCustomContentIds }) {
	return {
		title: data.title,
		content: reduce(data.content, (acc, info, lang) => ({
			...acc,
			[lang]: {
				...info,
				list: info.list.map((item) => {
					const type = item.type || bannerTypes.simple;
					if (type === bannerTypes.custom) {
						const content = find(bannerCustomContentIds, { id: item.contentId });
						return {
							...item,
							contentId: [{ value: content.id, label: content.text }],
							type: [{ value: type, label: type }]
						};
					}
					return {
						...item,
						type: [{ value: type, label: type }]
					};
				})
			}
		}), {})
	};
}

function getCreateBannerRequest(payload) {
	return http({
		url: endpoints.BANNERS.CONTENTS,
		method: 'post',
		actions: [SET_INITIAL_VALUES, FETCH_DATA_ERROR],
		data: payload
	});
}

function getUpdateBannerRequest(id, payload, lists) {
	return http({
		url: `${endpoints.BANNERS.CONTENTS}/${id}`,
		method: 'put',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		data: payload,
		addToResponse: {
			bannerCustomContentIds: lists['banner-custom-content-ids']
		}
	});
}

// actions
export const actions = {
	own: {
		fetchData: (props) => (dispatch, getState) => {
			const bannerId = props.params.id;

			if (!bannerId) {
				return dispatch({ type: SET_INITIAL_VALUES });
			}

			const lists = getState().root.lists;

			const request = http({
				url: `${endpoints.BANNERS.CONTENTS}/${bannerId}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
				addToResponse: {
					bannerCustomContentIds: lists['banner-custom-content-ids']
				}
			});

			return request(dispatch);
		},
		onSubmit: (values, _, props) => (dispatch, getState) => {
			validateBannerContent(values.content);

			const bannerId = props.params.id;

			dispatch(actions.own.toggleProgressBar());

			const payload = {
				title: values.title,
				content: reduce(values.content, (acc, info, lang) => ({
					...acc,
					[lang]: {
						list: info.list.map((item) => {
							const type = item.type[0].value;
							return type === bannerTypes.simple ? {
								type: bannerTypes.simple,
								...pick(item, ['image', 'link', 'blank'])
							} : {
								type: bannerTypes.custom,
								contentId: item.contentId[0].value
							};
						}),
						interval: info.list.length > 1 ? info.interval : null
					}
				}), {})
			};

			const request = bannerId
				? getUpdateBannerRequest(bannerId, payload, getState().root.lists)
				: getCreateBannerRequest(payload);

			return request(dispatch);
		},
		onChangeLang: (lang) => (dispatch, getState) => {
			dispatch({ type: CHANGE_LANGUAGE, lang });
		},
		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR })
	}
};

const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: mapResponseDataToStateData(action.payload.data, action.meta),
			isLoading: false,
			isError: false
		}),
		[SET_INITIAL_VALUES]: (state, action) => ({
			...state,
			data: initialFormValues,
			isLoading: false,
			isError: false
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state,
			isLoading: false,
			isError: true
		}),
		[CHANGE_LANGUAGE]: (state, action) => ({
			...state,
			language: action.lang
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state,
			isLoading: !state.isLoading
		})
	},
);

// reducers
export default handleActions(reducers, initialState);

// selectors
export const selectors = {
	select: (state, field) => state[namespace][field],
	getBannerCustomContentIds: state => state.root.lists['banner-custom-content-ids'],
	getBannerTypes: state => state.root.lists['banner-types'],
	getTabLangs: (state) => {
		const languages = state.root.lists.languages;
		const currentLang = state[namespace].language;

		return languages.map((lang) => ({
			id: lang.id,
			text: lang.text,
			isSelected: true,
			isActive: lang.id === currentLang,
			isDefault: lang.id === currentLang
		}));
	}
};

function validateBannerContent(content) {
	forEach(content, (info, lang) => {

		forEach(info.list, ({ type, image, link, contentId }, i) => {
			if (type[0].value === bannerTypes.simple) {
				if (!image) {
					throw new SubmissionError({
						content: {
							[lang]: {
								list: {
									[i]: {
										image: 'required'
									}
								}
							}
						},
						_error: `Image #${i + 1} is required for ${lang.toUpperCase()}`
					});
				}

				if (!patterns.url.test(image)) {
					throw new SubmissionError({
						content: {
							[lang]: {
								list: {
									[i]: {
										image: 'invalid url'
									}
								}
							}
						},
						_error: `Image #${i + 1} is invalid for ${lang.toUpperCase()}`
					});
				}

				if (!patterns.extensionsOf.images.test(image)) {
					throw new SubmissionError({
						content: {
							[lang]: {
								list: {
									[i]: {
										image: 'invalid extension'
									}
								}
							}
						},
						_error: `Image #${i + 1} extension is invalid for ${lang.toUpperCase()}`
					});
				}

				if (link && !patterns.url.test(link)) {
					throw new SubmissionError({
						content: {
							[lang]: {
								list: {
									[i]: {
										link: 'invalid url'
									}
								}
							}
						},
						_error: `Link #${i + 1} is invalid for ${lang.toUpperCase()}`
					});
				}
			}
			if (type[0].value === bannerTypes.custom) {
				if (!contentId) {
					throw new SubmissionError({
						content: {
							[lang]: {
								list: {
									[i]: {
										contentId: 'required'
									}
								}
							}
						},
						_error: `Content #${i + 1} is required for ${lang.toUpperCase()}`
					});
				}
			}
		});

		if (info.list.length > 1) {
			if (!info.interval) {
				throw new SubmissionError({
					content: {
						[lang]: {
							interval: 'required'
						}
					},
					_error: `Banner change time is required for ${lang.toUpperCase()}`
				});
			}

			if (isNaN(info.interval)) {
				throw new SubmissionError({
					content: {
						[lang]: {
							interval: 'incorrect'
						}
					},
					_error: `Banner change time is incorrect for ${lang.toUpperCase()}`
				});
			}

			if (info.interval < 500) {
				throw new SubmissionError({
					content: {
						[lang]: {
							interval: 'less than 500'
						}
					},
					_error: `Banner change time is less than 500 for ${lang.toUpperCase()}`
				});
			}
		}
	});
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/banners/content/editor/index.js